import React from 'react'
import {cn} from './cn'

// The one Fitro Button, shared by the Partner Dashboard and the Customer App (and their demos).
// Variants follow the design system: brand gradient, brand outline, quiet and danger.
export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'neutral'
    | 'ghost'
    | 'cancel'
    | 'danger'
    | 'error'
    | 'warning'
    | 'success'

export type ButtonSize = 'small' | 'medium' | 'large' | 'inline'

interface ButtonBaseProps {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    iconOnly?: boolean
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode
    children?: React.ReactNode
    className?: string
}

export interface ButtonProps extends ButtonBaseProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
}

// Props of the router link a LinkButton renders (next/link in the apps).
type RouterLinkProps = {href: unknown, className?: string, children?: React.ReactNode}

export type LinkButtonProps<P extends RouterLinkProps = RouterLinkProps> = ButtonBaseProps & Omit<P, 'className' | 'children'> & {
    disabled?: boolean
    /** Opens the link in a new tab as a plain anchor. */
    external?: boolean
}

const brandFocus = 'focus-visible:ring-2 focus-visible:ring-[var(--fitro-brand-primary-500)] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900'
const dangerFocus = 'focus-visible:ring-2 focus-visible:ring-[var(--fitro-action-danger)] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900'

const brandOutlineVariant = [
    'bg-white text-[var(--fitro-brand-primary-600)] border border-solid border-[var(--fitro-brand-primary-100)]',
    'hover:bg-[rgba(237,240,253,0.88)]',
    brandFocus,
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
    'dark:bg-transparent dark:text-[var(--fitro-brand-primary-100)] dark:border-[rgba(203,221,255,0.35)] dark:hover:bg-[rgba(203,221,255,0.08)] dark:disabled:hover:bg-transparent'
].join(' ')

const dangerVariant = [
    'bg-[var(--fitro-action-danger)] text-white shadow-[0_4px_12px_rgba(180,35,24,0.24)]',
    'hover:bg-[var(--fitro-action-danger-hover)]',
    'active:bg-[var(--fitro-action-danger-hover)]',
    dangerFocus,
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--fitro-action-danger)]',
    'dark:shadow-[0_4px_12px_rgba(0,0,0,0.28)]'
].join(' ')

const buttonVariants: Record<ButtonVariant, string> = {
    primary: [
        'bg-[linear-gradient(16deg,var(--fitro-brand-gradient-primary-600)_0%,var(--fitro-brand-gradient-secondary-500)_100%)] text-white shadow-[0_14px_28px_rgba(57,98,198,0.24)] dark:shadow-none',
        'hover:brightness-[1.04]',
        brandFocus,
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 dark:disabled:opacity-40 dark:disabled:saturate-[.35]'
    ].join(' '),

    secondary: brandOutlineVariant,

    outline: brandOutlineVariant,

    neutral: brandOutlineVariant,

    ghost: [
        'bg-transparent text-[var(--fitro-brand-neutral-700)] border-none shadow-none',
        'hover:bg-[var(--fitro-brand-neutral-50)] hover:text-[var(--fitro-brand-primary-600)]',
        brandFocus,
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-[var(--fitro-brand-primary-100)]'
    ].join(' '),

    cancel: [
        'bg-white text-[var(--fitro-action-danger)] border border-solid border-error-200 shadow-sm',
        'hover:bg-error-50',
        dangerFocus,
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
        'dark:bg-transparent dark:text-error-300 dark:border-[rgba(248,113,113,0.4)] dark:hover:bg-error-900/25 dark:disabled:hover:bg-transparent'
    ].join(' '),

    danger: dangerVariant,

    error: dangerVariant,

    // No orange action buttons: warning actions are consequential, so they use the danger style.
    warning: dangerVariant,

    success: [
        'bg-success-600 text-white shadow-[0_8px_20px_rgba(5,150,105,0.22)]',
        'hover:bg-success-700 hover:shadow-[0_10px_24px_rgba(4,120,87,0.28)] dark:shadow-none dark:hover:shadow-none',
        'active:bg-success-700',
        'focus-visible:ring-2 focus-visible:ring-success-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-success-600'
    ].join(' ')
}

const buttonSizes: Record<ButtonSize, string> = {
    inline: 'btn-micro min-h-0 min-w-0 p-0 text-sm leading-[inherit] rounded-sm align-baseline',
    small: 'px-4 py-2 text-body-small min-h-[32px] rounded-lg',
    medium: 'px-6 py-3 text-body min-h-[44px] rounded-lg',
    large: 'px-8 py-4 text-body-large min-h-[52px] rounded-lg'
}

const iconOnlySizes: Record<ButtonSize, string> = {
    inline: 'btn-micro min-h-0 min-w-0 p-0',
    small: 'p-2 aspect-square',
    medium: 'p-3 aspect-square',
    large: 'p-4 aspect-square'
}

const mobileSizes: Record<ButtonSize, string> = {
    inline: '',
    small: 'sm:min-h-[32px] min-h-[40px] sm:px-4 sm:py-2 px-5 py-3',
    medium: 'sm:min-h-[44px] min-h-[48px] sm:px-6 sm:py-3 px-6 py-4',
    large: 'sm:min-h-[52px] min-h-[56px] sm:px-8 sm:py-4 px-10 py-5'
}

const baseClasses = [
    'relative inline-flex items-center justify-center gap-2',
    'font-medium font-sans leading-none text-center select-none no-underline',
    'outline-hidden cursor-pointer',
    'transition-[color,background-color,border-color,box-shadow,scale,opacity,filter] duration-press ease-out',
    'focus:outline-hidden',
    'active:scale-[0.97] motion-reduce:active:scale-100 disabled:pointer-events-none disabled:active:scale-100'
].join(' ')

export const getButtonClasses = ({
    variant = 'primary',
    size = 'medium',
    loading = false,
    iconOnly = false,
    className
}: Pick<ButtonBaseProps, 'variant' | 'size' | 'loading' | 'iconOnly' | 'className'>) => cn(
    baseClasses,
    buttonVariants[variant],
    iconOnly ? iconOnlySizes[size] : buttonSizes[size],
    iconOnly ? '' : mobileSizes[size],
    loading && 'cursor-not-allowed',
    className
)

const renderButtonContent = ({
    leftIcon,
    rightIcon,
    children,
    loading = false
}: Pick<ButtonBaseProps, 'leftIcon' | 'rightIcon' | 'children' | 'loading'>) => (
    <>
        {leftIcon && !loading && (
            <span className="inline-flex items-center justify-center">
                {leftIcon}
            </span>
        )}
        <span className={cn('inline-flex items-center justify-center', loading && 'opacity-0')}>
            {children}
        </span>
        {rightIcon && !loading && (
            <span className="inline-flex items-center justify-center">
                {rightIcon}
            </span>
        )}
        {loading && (
            <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]"/>
            </span>
        )}
    </>
)

export const Button = React.memo(React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({
         variant = 'primary',
         size = 'medium',
         loading = false,
         iconOnly = false,
         leftIcon,
         rightIcon,
         children,
         className,
         disabled,
         ...props
     }, ref) => (
        <button
            ref={ref}
            className={getButtonClasses({variant, size, loading, iconOnly, className})}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...props}
        >
            {renderButtonContent({leftIcon, rightIcon, children, loading})}
        </button>
    )
))

Button.displayName = 'Button'

// Shared UI stays framework-free, so each app package builds its LinkButton from its router link:
// `export const LinkButton = createLinkButton(Link)` with next/link.
export function createLinkButton<P extends RouterLinkProps>(RouterLink: React.ComponentType<P & React.RefAttributes<HTMLAnchorElement>>) {
    const LinkButton = React.memo(React.forwardRef<HTMLAnchorElement, LinkButtonProps<P>>(
        (rawProps, ref) => {
            const {
                variant = 'primary',
                size = 'medium',
                loading = false,
                iconOnly = false,
                leftIcon,
                rightIcon,
                children,
                className,
                disabled,
                external = false,
                ...props
            } = rawProps as ButtonBaseProps & {disabled?: boolean, external?: boolean} & Record<string, unknown>
            const classes = getButtonClasses({variant, size, loading, iconOnly, className})
            const content = renderButtonContent({leftIcon, rightIcon, children, loading})
            const linkProps = props as unknown as Omit<P, 'className' | 'children'> & React.AnchorHTMLAttributes<HTMLAnchorElement>

            if (disabled) {
                return (
                    <span
                        aria-disabled="true"
                        aria-busy={loading || undefined}
                        aria-label={linkProps['aria-label']}
                        title={linkProps.title}
                        className={cn(classes, 'pointer-events-none opacity-50 cursor-not-allowed')}
                    >
                        {content}
                    </span>
                )
            }

            // Keep the same focusable anchor while preparing; aria-disabled alone does not block activation.
            const busyProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = loading ? {
                'aria-disabled': true,
                'aria-busy': true,
                onClick: event => { event.preventDefault(); event.stopPropagation() },
                onAuxClick: event => { event.preventDefault(); event.stopPropagation() },
            } : {}

            if (external) {
                const {href, prefetch: _prefetch, replace: _replace, scroll: _scroll, shallow: _shallow, ...anchorProps} = linkProps as typeof linkProps & {href: unknown, prefetch?: unknown, replace?: unknown, scroll?: unknown, shallow?: unknown}
                return (
                    <a ref={ref} href={String(href)} target="_blank" rel="noopener noreferrer" className={classes} {...anchorProps} {...busyProps}>
                        {content}
                    </a>
                )
            }

            const routerProps = {...linkProps, ...busyProps, ref, className: classes, children: content} as unknown as P & React.RefAttributes<HTMLAnchorElement>
            return <RouterLink {...routerProps}/>
        }
    ))
    LinkButton.displayName = 'LinkButton'
    return LinkButton
}
